import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
	ReactNode,
} from 'react'

import { Campaign } from '@/core/api/entities/campaign/campaign'
import { Group } from '@/core/api/entities/group'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Mix } from '@/core/api/entities/radio/mix'
import { Schedule } from '@/core/api/entities/schedule'
import { Zone } from '@/core/api/entities/zone/zone'

interface GroupMetadata {
	name: string
	accountId: number
	musicProfileId: number | null
}

interface ContentDraft<T> {
	original: T[]
	current: T[]
	added: number[]
	removed: number[]
}

interface MixDraft {
	original: Mix[]
	current: Mix[]
	added: Mix[]
	removed: number[]
	updated: Mix[]
}

interface GroupDraftState {
	original: Group | null
	metadata: GroupMetadata
	zones: ContentDraft<Zone>
	schedules: ContentDraft<Schedule>
	campaigns: ContentDraft<Campaign>
	playlists: ContentDraft<Playlist>
	mixes: MixDraft
	hasChanges: boolean
	isSaving: boolean
}

interface GroupDraftContextType extends GroupDraftState {
	updateMetadata: (data: Partial<GroupMetadata>) => void
	setZones: (zones: Zone[]) => void
	addZones: (zones: Zone[]) => void
	removeZones: (zoneIds: number[]) => void
	setSchedules: (schedules: Schedule[]) => void
	addSchedules: (schedules: Schedule[]) => void
	removeSchedules: (scheduleIds: number[]) => void
	setCampaigns: (campaigns: Campaign[]) => void
	addCampaigns: (campaigns: Campaign[]) => void
	removeCampaigns: (campaignIds: number[]) => void
	setPlaylists: (playlists: Playlist[]) => void
	addPlaylists: (playlists: Playlist[]) => void
	removePlaylists: (playlistIds: number[]) => void
	setMixes: (mixes: Mix[]) => void
	addMixes: (mixes: Mix[]) => void
	removeMixes: (mixIds: number[]) => void
	updateMix: (mix: Mix) => void
	resetDraft: () => void
	setSaving: (isSaving: boolean) => void
	updateWithFreshData: (group: Group) => void
	getChanges: () => {
		metadata: boolean
		zones: { added: number[]; removed: number[] }
		schedules: { added: number[]; removed: number[] }
		campaigns: { added: number[]; removed: number[] }
		playlists: { added: number[]; removed: number[] }
		mixes: { added: Mix[]; removed: number[]; updated: Mix[] }
	}
}

const GroupDraftContext = createContext<GroupDraftContextType | undefined>(
	undefined
)

interface GroupDraftProviderProps {
	children: ReactNode
	initialGroup: Group | null
}

const createInitialState = (group: Group | null): GroupDraftState => {
	if (!group) {
		return {
			original: null,
			metadata: { name: '', accountId: 0, musicProfileId: null },
			zones: { original: [], current: [], added: [], removed: [] },
			schedules: { original: [], current: [], added: [], removed: [] },
			campaigns: { original: [], current: [], added: [], removed: [] },
			playlists: { original: [], current: [], added: [], removed: [] },
			mixes: { original: [], current: [], added: [], removed: [], updated: [] },
			hasChanges: false,
			isSaving: false,
		}
	}

	return {
		original: group,
		metadata: {
			name: group.name,
			accountId: group.accountId,
			musicProfileId: group.musicProfileId,
		},
		zones: {
			original: group.zones || [],
			current: group.zones || [],
			added: [],
			removed: [],
		},
		schedules: {
			original: group.schedules || [],
			current: group.schedules || [],
			added: [],
			removed: [],
		},
		campaigns: {
			original: group.campaigns || [],
			current: group.campaigns || [],
			added: [],
			removed: [],
		},
		playlists: {
			original: group.playlists || [],
			current: group.playlists || [],
			added: [],
			removed: [],
		},
		mixes: {
			original: group.mixes || [],
			current: group.mixes || [],
			added: [],
			removed: [],
			updated: [],
		},
		hasChanges: false,
		isSaving: false,
	}
}

export const GroupDraftProvider: React.FC<GroupDraftProviderProps> = ({
	children,
	initialGroup,
}) => {
	const [state, setState] = useState<GroupDraftState>(
		createInitialState(initialGroup)
	)

	const checkHasChanges = useCallback(
		(
			currentState: GroupDraftState,
			newState: Partial<GroupDraftState> = {}
		): boolean => {
			const s = { ...currentState, ...newState }

			if (!s.original) return true

			const metadataChanged =
				s.metadata.name !== s.original.name ||
				s.metadata.accountId !== s.original.accountId ||
				s.metadata.musicProfileId !== s.original.musicProfileId

			const zonesChanged =
				s.zones.added.length > 0 || s.zones.removed.length > 0
			const schedulesChanged =
				s.schedules.added.length > 0 || s.schedules.removed.length > 0
			const campaignsChanged =
				s.campaigns.added.length > 0 || s.campaigns.removed.length > 0
			const playlistsChanged =
				s.playlists.added.length > 0 || s.playlists.removed.length > 0
			const mixesChanged =
				s.mixes.added.length > 0 ||
				s.mixes.removed.length > 0 ||
				s.mixes.updated.length > 0

			return (
				metadataChanged ||
				zonesChanged ||
				schedulesChanged ||
				campaignsChanged ||
				playlistsChanged ||
				mixesChanged
			)
		},
		[]
	)

	const updateMetadata = useCallback(
		(data: Partial<GroupMetadata>) => {
			setState((prev) => {
				const newMetadata = { ...prev.metadata, ...data }
				const hasChanges = checkHasChanges(prev, { metadata: newMetadata })
				return { ...prev, metadata: newMetadata, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const setZones = useCallback(
		(zones: Zone[]) => {
			setState((prev) => {
				const originalIds = prev.zones.original.map((z) => z.id)
				const currentIds = zones.map((z) => z.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))
				const newZones = { ...prev.zones, current: zones, added, removed }
				const hasChanges = checkHasChanges(prev, { zones: newZones })
				return { ...prev, zones: newZones, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const addZones = useCallback(
		(zones: Zone[]) => {
			setState((prev) => {
				const currentIds = prev.zones.current.map((z) => z.id)
				const newZones = zones.filter((z) => !currentIds.includes(z.id))
				const updatedCurrent = [...prev.zones.current, ...newZones]

				const originalIds = prev.zones.original.map((z) => z.id)
				const currentIds2 = updatedCurrent.map((z) => z.id)
				const added = currentIds2.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds2.includes(id))

				const newZonesState = {
					...prev.zones,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { zones: newZonesState })
				return { ...prev, zones: newZonesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const removeZones = useCallback(
		(zoneIds: number[]) => {
			setState((prev) => {
				const updatedCurrent = prev.zones.current.filter(
					(z) => !zoneIds.includes(z.id)
				)

				const originalIds = prev.zones.original.map((z) => z.id)
				const currentIds = updatedCurrent.map((z) => z.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))

				const newZonesState = {
					...prev.zones,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { zones: newZonesState })
				return { ...prev, zones: newZonesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const setSchedules = useCallback(
		(schedules: Schedule[]) => {
			setState((prev) => {
				const originalIds = prev.schedules.original.map((s) => s.id)
				const currentIds = schedules.map((s) => s.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))
				const newSchedules = {
					...prev.schedules,
					current: schedules,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { schedules: newSchedules })
				return { ...prev, schedules: newSchedules, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const addSchedules = useCallback(
		(schedules: Schedule[]) => {
			setState((prev) => {
				const currentIds = prev.schedules.current.map((s) => s.id)
				const newSchedules = schedules.filter((s) => !currentIds.includes(s.id))
				const updatedCurrent = [...prev.schedules.current, ...newSchedules]

				const originalIds = prev.schedules.original.map((s) => s.id)
				const currentIds2 = updatedCurrent.map((s) => s.id)
				const added = currentIds2.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds2.includes(id))

				const newSchedulesState = {
					...prev.schedules,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, {
					schedules: newSchedulesState,
				})
				return { ...prev, schedules: newSchedulesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const removeSchedules = useCallback(
		(scheduleIds: number[]) => {
			setState((prev) => {
				const updatedCurrent = prev.schedules.current.filter(
					(s) => !scheduleIds.includes(s.id)
				)

				const originalIds = prev.schedules.original.map((s) => s.id)
				const currentIds = updatedCurrent.map((s) => s.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))

				const newSchedulesState = {
					...prev.schedules,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, {
					schedules: newSchedulesState,
				})
				return { ...prev, schedules: newSchedulesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const setCampaigns = useCallback(
		(campaigns: Campaign[]) => {
			setState((prev) => {
				const originalIds = prev.campaigns.original.map((c) => c.id)
				const currentIds = campaigns.map((c) => c.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))
				const newCampaigns = {
					...prev.campaigns,
					current: campaigns,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { campaigns: newCampaigns })
				return { ...prev, campaigns: newCampaigns, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const addCampaigns = useCallback(
		(campaigns: Campaign[]) => {
			setState((prev) => {
				const currentIds = prev.campaigns.current.map((c) => c.id)
				const newCampaigns = campaigns.filter((c) => !currentIds.includes(c.id))
				const updatedCurrent = [...prev.campaigns.current, ...newCampaigns]

				const originalIds = prev.campaigns.original.map((c) => c.id)
				const currentIds2 = updatedCurrent.map((c) => c.id)
				const added = currentIds2.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds2.includes(id))

				const newCampaignsState = {
					...prev.campaigns,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, {
					campaigns: newCampaignsState,
				})
				return { ...prev, campaigns: newCampaignsState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const removeCampaigns = useCallback(
		(campaignIds: number[]) => {
			setState((prev) => {
				const updatedCurrent = prev.campaigns.current.filter(
					(c) => !campaignIds.includes(c.id)
				)

				const originalIds = prev.campaigns.original.map((c) => c.id)
				const currentIds = updatedCurrent.map((c) => c.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))

				const newCampaignsState = {
					...prev.campaigns,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, {
					campaigns: newCampaignsState,
				})
				return { ...prev, campaigns: newCampaignsState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const setPlaylists = useCallback(
		(playlists: Playlist[]) => {
			setState((prev) => {
				const originalIds = prev.playlists.original.map((p) => p.id)
				const currentIds = playlists.map((p) => p.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))
				const newPlaylists = {
					...prev.playlists,
					current: playlists,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { playlists: newPlaylists })
				return { ...prev, playlists: newPlaylists, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const addPlaylists = useCallback(
		(playlists: Playlist[]) => {
			setState((prev) => {
				const currentIds = prev.playlists.current.map((p) => p.id)
				const newPlaylists = playlists.filter((p) => !currentIds.includes(p.id))
				const updatedCurrent = [...prev.playlists.current, ...newPlaylists]

				const originalIds = prev.playlists.original.map((p) => p.id)
				const currentIds2 = updatedCurrent.map((p) => p.id)
				const added = currentIds2.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds2.includes(id))

				const newPlaylistsState = {
					...prev.playlists,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, {
					playlists: newPlaylistsState,
				})
				return { ...prev, playlists: newPlaylistsState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const removePlaylists = useCallback(
		(playlistIds: number[]) => {
			setState((prev) => {
				const updatedCurrent = prev.playlists.current.filter(
					(p) => !playlistIds.includes(p.id)
				)

				const originalIds = prev.playlists.original.map((p) => p.id)
				const currentIds = updatedCurrent.map((p) => p.id)
				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))

				const newPlaylistsState = {
					...prev.playlists,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, {
					playlists: newPlaylistsState,
				})
				return { ...prev, playlists: newPlaylistsState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const setMixes = useCallback(
		(mixes: Mix[]) => {
			setState((prev) => {
				const originalIds = prev.mixes.original.map((m) => m.id)
				const currentMixes = mixes

				const added = currentMixes.filter((m) => !originalIds.includes(m.id))
				const removed = originalIds.filter(
					(id) => !currentMixes.find((m) => m.id === id)
				)
				const updated: Mix[] = []

				const newMixes = {
					...prev.mixes,
					current: currentMixes,
					added,
					removed,
					updated,
				}
				const hasChanges = checkHasChanges(prev, { mixes: newMixes })
				return { ...prev, mixes: newMixes, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const addMixes = useCallback(
		(mixes: Mix[]) => {
			setState((prev) => {
				const currentIds = prev.mixes.current.map((m) => m.id)
				const newMixes = mixes.filter((m) => !currentIds.includes(m.id))
				const updatedCurrent = [...prev.mixes.current, ...newMixes]

				const originalIds = prev.mixes.original.map((m) => m.id)
				const added = updatedCurrent.filter((m) => !originalIds.includes(m.id))
				const removed = originalIds.filter(
					(id) => !updatedCurrent.find((m) => m.id === id)
				)

				const newMixesState = {
					...prev.mixes,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { mixes: newMixesState })
				return { ...prev, mixes: newMixesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const removeMixes = useCallback(
		(mixIds: number[]) => {
			setState((prev) => {
				const updatedCurrent = prev.mixes.current.filter(
					(m) => !mixIds.includes(m.id)
				)

				const originalIds = prev.mixes.original.map((m) => m.id)
				const added = updatedCurrent.filter((m) => !originalIds.includes(m.id))
				const removed = originalIds.filter(
					(id) => !updatedCurrent.find((m) => m.id === id)
				)

				const newMixesState = {
					...prev.mixes,
					current: updatedCurrent,
					added,
					removed,
				}
				const hasChanges = checkHasChanges(prev, { mixes: newMixesState })
				return { ...prev, mixes: newMixesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const updateMix = useCallback(
		(mix: Mix) => {
			setState((prev) => {
				const updatedCurrent = prev.mixes.current.map((m) =>
					m.id === mix.id ? mix : m
				)

				const originalIds = prev.mixes.original.map((m) => m.id)
				const added = updatedCurrent.filter((m) => !originalIds.includes(m.id))
				const removed = originalIds.filter(
					(id) => !updatedCurrent.find((m) => m.id === id)
				)

				const isOriginal = prev.mixes.original.some((m) => m.id === mix.id)
				const updated = isOriginal
					? [...prev.mixes.updated.filter((m) => m.id !== mix.id), mix]
					: prev.mixes.updated

				const newMixesState = {
					...prev.mixes,
					current: updatedCurrent,
					added,
					removed,
					updated,
				}
				const hasChanges = checkHasChanges(prev, { mixes: newMixesState })
				return { ...prev, mixes: newMixesState, hasChanges }
			})
		},
		[checkHasChanges]
	)

	const resetDraft = useCallback(() => {
		setState(createInitialState(state.original))
	}, [state.original])

	const setSaving = useCallback((isSaving: boolean) => {
		setState((prev) => ({ ...prev, isSaving }))
	}, [])

	const updateWithFreshData = useCallback((group: Group) => {
		setState(createInitialState(group))
	}, [])

	const getChanges = useCallback(() => {
		const metadataChanged =
			state.original &&
			(state.metadata.name !== state.original.name ||
				state.metadata.accountId !== state.original.accountId ||
				state.metadata.musicProfileId !== state.original.musicProfileId)

		return {
			metadata: !!metadataChanged,
			zones: { added: state.zones.added, removed: state.zones.removed },
			schedules: {
				added: state.schedules.added,
				removed: state.schedules.removed,
			},
			campaigns: {
				added: state.campaigns.added,
				removed: state.campaigns.removed,
			},
			playlists: {
				added: state.playlists.added,
				removed: state.playlists.removed,
			},
			mixes: {
				added: state.mixes.added,
				removed: state.mixes.removed,
				updated: state.mixes.updated,
			},
		}
	}, [state])

	const contextValue = useMemo(
		() => ({
			...state,
			updateMetadata,
			setZones,
			addZones,
			removeZones,
			setSchedules,
			addSchedules,
			removeSchedules,
			setCampaigns,
			addCampaigns,
			removeCampaigns,
			setPlaylists,
			addPlaylists,
			removePlaylists,
			setMixes,
			addMixes,
			removeMixes,
			updateMix,
			resetDraft,
			setSaving,
			updateWithFreshData,
			getChanges,
		}),
		[
			state,
			updateMetadata,
			setZones,
			addZones,
			removeZones,
			setSchedules,
			addSchedules,
			removeSchedules,
			setCampaigns,
			addCampaigns,
			removeCampaigns,
			setPlaylists,
			addPlaylists,
			removePlaylists,
			setMixes,
			addMixes,
			removeMixes,
			updateMix,
			resetDraft,
			setSaving,
			updateWithFreshData,
			getChanges,
		]
	)

	return (
		<GroupDraftContext.Provider value={contextValue}>
			{children}
		</GroupDraftContext.Provider>
	)
}

export const useGroupDraft = (): GroupDraftContextType => {
	const context = useContext(GroupDraftContext)
	if (!context) {
		throw new Error('useGroupDraft must be used within a GroupDraftProvider')
	}
	return context
}
